import React from 'react';
import { 
  Box, 
  Chip,
  Typography,
  Button
} from '@mui/material';
import { useTheme } from '@emotion/react';
import { tokens } from '../theme';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';

/**
 * Component to display and manage active filters
 * 
 * @param {Object} props
 * @param {Array} props.filters - List of active filters
 * @param {function} props.onRemoveFilter - Function called when a filter is removed
 * @param {function} props.onClearFilters - Function called when all filters are cleared
 * @param {Array} props.columns - Available columns info for display labels
 */
const ActiveFilters = ({
  filters = [],
  onRemoveFilter,
  onClearFilters,
  columns = []
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // If no filters, don't render anything
  if (!filters || filters.length === 0) return null;
  
  // Helper to get column display name
  const getColumnLabel = (columnId) => {
    const column = columns.find(col => col.id === columnId);
    return column ? (column.header || column.id) : columnId;
  };
  
  // Helper to get a readable operator string
  const getOperatorLabel = (operator) => {
    const operatorLabels = {
      contains: 'contains',
      equals: 'equals',
      startsWith: 'starts with',
      endsWith: 'ends with',
      greaterThan: '> than',
      lessThan: '< than'
    };
    
    return operatorLabels[operator] || operator;
  };
  
  return (
    <Box sx={{ mt: 1, mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
      <Typography 
        variant="body2" 
        sx={{ 
          fontWeight: 500,
          color: colors.grey[700],
          mr: 1
        }}
      >
        Active Filters:
      </Typography>
      
      {filters.map((filter, index) => (
        <Chip
          key={`${filter.id || index}`}
          label={`${getColumnLabel(filter.column)} ${getOperatorLabel(filter.operator)} "${filter.value}"`}
          onDelete={() => onRemoveFilter(filter.id || index)}
          size="small"
          sx={{
            borderRadius: '4px',
            bgcolor: colors.primary[50],
            color: colors.primary[700],
            '& .MuiChip-deleteIcon': {
              color: colors.primary[500],
              '&:hover': {
                color: colors.primary[700],
              },
            },
          }}
        />
      ))}
      
      {filters.length > 1 && (
        <Button
          startIcon={<FilterListOffIcon />}
          onClick={onClearFilters}
          size="small"
          sx={{ 
            ml: 1,
            color: colors.grey[600],
            '&:hover': {
              backgroundColor: colors.grey[100],
            }
          }}
        >
          Clear All
        </Button>
      )}
    </Box>
  );
};

export default ActiveFilters;
